
import { useState } from 'react';
import Sidebar from '../components/Sidebar';

interface InventoryItem {
  id: number;
  itemCode: string;
  itemName: string;
  category: string;
  status: string;
  vendor: string;
  manager: string;
  receiveDate: string;
  returnDate: string;
  customerName: string;
  notes: string;
}

export default function InventoryManagement() {
  const [selectedStatus, setSelectedStatus] = useState('전체');
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [selectedVendor, setSelectedVendor] = useState('전체');
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [inventory] = useState<InventoryItem[]>([
    {
      id: 1,
      itemCode: 'WD-001',
      itemName: '웨딩드레스',
      category: '웨딩드레스',
      status: '입고완료',
      vendor: '로즈마리드레스',
      manager: '김플래너',
      receiveDate: '2024-03-15',
      returnDate: '2024-04-20',
      customerName: '신혜림',
      notes: '고객 피팅 완료, 수선 필요없음'
    },
    {
      id: 2,
      itemCode: 'TX-001',
      itemName: '턱시도',
      category: '턱시도',
      status: '출고대기',
      vendor: '신사복하우스',
      manager: '이플래너',
      receiveDate: '2024-03-16',
      returnDate: '2024-04-20',
      customerName: '박지훈',
      notes: '바지 기장 수선 중'
    },
    {
      id: 3,
      itemCode: 'WD-002',
      itemName: '웨딩드레스',
      category: '웨딩드레스',
      status: '출고완료',
      vendor: '프리미스 라인',
      manager: '김플래너',
      receiveDate: '2024-02-01',
      returnDate: '2024-03-20',
      customerName: '김민정',
      notes: '수정 완료'
    }
  ]);

  const statuses = ['전체', '입고대기', '입고완료', '제작중', '출고대기', '출고완료'];
  const categories = ['전체', '웨딩드레스', '턱시도', '부케', '예약금'];
  const vendors = ['전체', '웨드드레스', '턱시도', '부케', '예약금', '소품'];

  const filteredInventory = inventory.filter(item => {
    const statusMatch = selectedStatus === '전체' || item.status === selectedStatus;
    const categoryMatch = selectedCategory === '전체' || item.category === selectedCategory;
    const vendorMatch = selectedVendor === '전체' || item.category === selectedVendor;
    return statusMatch && categoryMatch && vendorMatch;
  });

  const getStatusCount = (status: string) => {
    return inventory.filter(item => item.status === status).length;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case '입고완료':
        return 'bg-green-100 text-green-800';
      case '출고대기':
        return 'bg-yellow-100 text-yellow-800';
      case '출고완료':
        return 'bg-gray-100 text-gray-800';
      case '제작중':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">입출고관리</h1>
              <p className="text-sm text-gray-600 mt-1">상품별 작업상황을 체크하고 관리하세요</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              입출고 등록
            </button>
          </div>

          {/* 통계 카드 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-5 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">입고대기</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{getStatusCount('입고대기')}건</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-inbox-line text-blue-600 text-xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">입고완료</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{getStatusCount('입고완료')}건</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">제작중</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{getStatusCount('제작중')}건</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <i className="ri-tools-line text-purple-600 text-xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">출고완료</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{getStatusCount('출고완료')}건</p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <i className="ri-truck-line text-gray-600 text-xl"></i>
                </div>
              </div>
            </div>
          </div>

          {/* 필터 */}
          <div className="bg-white p-4 rounded-lg shadow mb-6">
            <div className="flex flex-wrap gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">진행상태:</label>
                <div className="flex flex-wrap gap-2">
                  {statuses.map(status => (
                    <button
                      key={status}
                      onClick={() => setSelectedStatus(status)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                        selectedStatus === status
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">상품 구분:</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                        selectedCategory === category
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 입출고 목록 */}
          <div className="grid grid-cols-1 gap-4">
            {filteredInventory.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <i className="ri-shopping-bag-line text-purple-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{item.itemName}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-gray-600">{item.vendor}</span>
                        <span className="text-gray-300">|</span>
                        <span className="text-sm text-gray-600">담당자: {item.manager}</span>
                      </div>
                    </div>
                  </div>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(item.status)}`}>
                    {item.status}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-6 mb-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">고객정보</p>
                    <p className="text-base font-medium text-gray-900">{item.customerName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">업체정보</p>
                    <p className="text-base font-medium text-gray-900">{item.vendor}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">담당자</p>
                    <p className="text-base font-medium text-gray-900">{item.manager}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">입출고일</p>
                    <p className="text-base font-medium text-gray-900">입출고일: {item.receiveDate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">완성정보</p>
                    <p className="text-base font-medium text-gray-900">본식일: {item.returnDate}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-1">특이사항</p>
                  <p className="text-base text-gray-900">{item.notes}</p>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors whitespace-nowrap flex items-center gap-1">
                    <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                    수정
                  </button>
                  <button className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors whitespace-nowrap flex items-center gap-1">
                    <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                    연락
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* 입출고 등록 모달 */}
          {showAddForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">입출고 등록</h2>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
                
                <form className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">상품코드</label>
                      <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">상품명</label>
                      <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">카테고리</label>
                      <select className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8">
                        {categories.slice(1).map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">업체명</label>
                      <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">고객명</label>
                      <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">담당자</label>
                      <select className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8">
                        <option>김플래너</option>
                        <option>박플래너</option>
                        <option>최플래너</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">입출고일</label>
                      <input type="date" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">본식일</label>
                      <input type="date" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">특이사항</label>
                    <textarea 
                      className="w-full border border-gray-300 rounded-md px-3 py-2 h-24"
                      placeholder="특이사항을 입력하세요"
                    ></textarea>
                  </div>
                  
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap"
                    >
                      등록
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
